import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  FileText, Plus, Edit2, Trash2, Eye, Send, RotateCcw,
  ChevronDown, ChevronUp, Settings2, Bell, ClipboardCheck,
  CheckCircle2, Clock, AlertCircle, Calendar, User,
  Download, Printer, CheckCheck, X, Save,
  BarChart2, Target, Users, TrendingUp,
} from 'lucide-react';
import { dailyReportsAPI, employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { YearContext } from '../components/Layout/Layout';
import StatCard from '../components/Cards/StatCard';
import { BarChartComponent, LineChartComponent, PieChartComponent } from '../components/Charts/ChartComponents';

// ─── constants ───────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  "Principal's Report", 'Vice Principal Report', 'Coordinator Report',
  'HOD Report', 'Department Meeting', 'Staff Meeting',
  'Management Report', 'Board Meeting', 'Other',
];

const FOLLOW_UP_OPTIONS = [
  { value: 'pending',     label: 'Pending',     color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'in_progress', label: 'In Progress',  color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'completed',   label: 'Completed',   color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'overdue',     label: 'Overdue',     color: 'bg-red-50 text-red-700 border-red-200' },
];

const TABS = [
  { id: 'dashboard',     label: 'Dashboard',     icon: BarChart2  },
  { id: 'daily_reports', label: 'Daily Reports',  icon: FileText   },
  { id: 'setup',         label: 'Setup',          icon: Settings2  },
];

const fi = 'border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

// ─── helpers ──────────────────────────────────────────────────────────────────
const toDateStr = (d) => !d ? '' : (d instanceof Date ? d.toISOString().split('T')[0] : String(d).split('T')[0]);
const fmtDate = (d) => {
  const s = toDateStr(d);
  return s ? new Date(s + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return ''; }
};

const parseRemarks = (raw) => {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return arr;
    return [{ text: String(raw), by: null, at: null }];
  } catch {
    return [{ text: String(raw), by: null, at: null }];
  }
};

const statusPill = (s) =>
  s === 'published'
    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    : 'bg-slate-100 text-slate-600 border border-slate-200';

const followUpColor = (s) =>
  FOLLOW_UP_OPTIONS.find(o => o.value === s)?.color || 'bg-slate-100 text-slate-500 border-slate-200';

// ─── empty row template ───────────────────────────────────────────────────────
const emptyItem = () => ({
  topic: '', discussion_summary: '', action_points: '', remarks: '',
  due_date: '', assigned_to: '', follow_up_status: 'pending',
});

// ─── ActionPoints renderer (bold **text** → <b> and bullets) ─────────────────
function ActionText({ text }) {
  if (!text) return <span className="text-slate-400">—</span>;
  return (
    <div className="space-y-0.5">
      {text.split('\n').map((line, i) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} className="text-sm leading-relaxed">
            {parts.map((p, j) =>
              p.startsWith('**') && p.endsWith('**')
                ? <strong key={j}>{p.slice(2, -2)}</strong>
                : p
            )}
          </p>
        );
      })}
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, add };
}

function ToastList({ toasts }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map(t => (
        <div key={t.id} className={`px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium border
          ${t.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ─── Report Form ──────────────────────────────────────────────────────────────
function ReportForm({ initial, onSave, onCancel }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState(initial || {
    reportType: "Principal's Report",
    reportDate: today,
    title: '',
    notes: '',
    status: 'draft',
    items: [emptyItem()],
  });
  const [employees,    setEmployees]    = useState([]);
  const [stagedFiles,  setStagedFiles]  = useState([]);
  const fileRef = React.useRef();

  useEffect(() => {
    employeesAPI.getAll()
      .then(r => setEmployees(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const sf = (patch) => setForm(f => ({ ...f, ...patch }));

  const setItem = (idx, patch) =>
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));

  const addRow = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));

  const removeRow = (idx) =>
    setForm(f => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));

  const insertBullet = (idx, field) => {
    const current = form.items[idx][field];
    const newVal = current ? current + '\n• ' : '• ';
    setItem(idx, { [field]: newVal });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setStagedFiles(prev => [...prev, ...files.map(f => ({ name: f.name, size: f.size, file: f }))]);
    e.target.value = '';
  };

  const removeStagedFile = (i) => setStagedFiles(prev => prev.filter((_, j) => j !== i));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

      {/* ── Header band ───────────────────────────────────── */}
      <div className="px-6 py-4 bg-gradient-to-r from-primary-50 to-slate-50 border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Report Type</label>
            <select className={`${fi} min-w-[180px]`} value={form.reportType} onChange={e => sf({ reportType: e.target.value })}>
              {REPORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Date</label>
            <input type="date" className={`${fi}`} value={form.reportDate} onChange={e => sf({ reportDate: e.target.value })} />
          </div>
          <div className="flex flex-col gap-0.5 flex-1 min-w-[200px]">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Title</label>
            <input className={`${fi} w-full`} placeholder="Meeting title (optional)" value={form.title} onChange={e => sf({ title: e.target.value })} />
          </div>
          <div className="flex gap-2 ml-auto pt-4">
            <button onClick={onCancel}
              className="px-4 py-2 text-sm border border-slate-200 bg-white rounded-lg hover:bg-slate-50 text-slate-600 flex items-center gap-1.5 shadow-sm">
              <X size={14} /> Cancel
            </button>
            <button onClick={() => onSave({ ...form, status: 'draft', stagedFiles })}
              className="px-4 py-2 text-sm border border-slate-200 bg-white rounded-lg hover:bg-slate-50 text-slate-700 flex items-center gap-1.5 shadow-sm">
              <Save size={14} /> Save Draft
            </button>
            <button onClick={() => onSave({ ...form, status: 'published', stagedFiles })}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-1.5 shadow-sm font-medium">
              <Send size={14} /> Publish
            </button>
          </div>
        </div>
      </div>

      {/* ── Agenda items ──────────────────────────────────── */}
      <div className="divide-y divide-slate-100">
        {/* Column labels (shown once above all rows) */}
        <div className="px-6 py-2 bg-slate-50 grid grid-cols-[2rem_1fr_1.4fr_1.4fr] gap-4">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">#</span>
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Topic</span>
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Discussion Summary</span>
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Action Points</span>
        </div>

        {form.items.map((item, idx) => (
          <div key={idx} className="px-6 py-4 hover:bg-slate-50/60 transition-colors">
            {/* Top row: #  Topic  Summary  Action Points */}
            <div className="grid grid-cols-[2rem_1fr_1.4fr_1.4fr] gap-4 items-start">
              <span className="mt-2 w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                {idx + 1}
              </span>
              <textarea className={`${fi} w-full resize-none`} rows={3}
                placeholder="e.g. Nanny Allocation &amp; Deployment"
                value={item.topic}
                onChange={e => setItem(idx, { topic: e.target.value })} />
              <textarea className={`${fi} w-full resize-none`} rows={3}
                placeholder="Discussion held regarding…"
                value={item.discussion_summary}
                onChange={e => setItem(idx, { discussion_summary: e.target.value })} />
              <div className="space-y-1">
                <textarea className={`${fi} w-full resize-none`} rows={3}
                  placeholder="• Action item 1&#10;• Action item 2"
                  value={item.action_points}
                  onChange={e => setItem(idx, { action_points: e.target.value })} />
                <button type="button" onClick={() => insertBullet(idx, 'action_points')}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium">+ bullet</button>
              </div>
            </div>

            {/* Bottom row: Due Date | Assigned To | Delete */}
            <div className="mt-3 ml-10 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar size={13} className="text-slate-400 flex-shrink-0" />
                <label className="text-xs text-slate-500 font-medium whitespace-nowrap">Due Date</label>
                <input type="date" className={`${fi} text-sm py-1`}
                  value={item.due_date}
                  onChange={e => setItem(idx, { due_date: e.target.value })} />
              </div>
              <div className="flex items-center gap-2">
                <User size={13} className="text-slate-400 flex-shrink-0" />
                <label className="text-xs text-slate-500 font-medium whitespace-nowrap">Assigned To</label>
                <select className={`${fi} text-sm py-1 min-w-[180px]`}
                  value={item.assigned_to}
                  onChange={e => setItem(idx, { assigned_to: e.target.value })}>
                  <option value="">— Select person —</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.name}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <button onClick={() => removeRow(idx)}
                className="ml-auto p-1.5 hover:bg-red-50 text-red-400 rounded-lg transition-colors"
                title="Remove row">
                <X size={14} />
              </button>
            </div>
            {/* Remarks + Upload File */}
            <div className="mt-2 ml-10 flex gap-2 items-start">
              <textarea className={`${fi} flex-1 resize-none text-sm`} rows={2}
                placeholder="Remarks / additional notes for this item…"
                value={item.remarks}
                onChange={e => setItem(idx, { remarks: e.target.value })} />
              <button type="button" onClick={() => fileRef.current?.click()}
                className="px-3 py-2 text-xs rounded-lg flex items-center gap-1.5 font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors whitespace-nowrap flex-shrink-0">
                <Plus size={12} /> Upload File
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Footer ────────────────────────────────────────── */}
      <input ref={fileRef} type="file" className="hidden" onChange={handleFileSelect} />
      <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-4">
        <button onClick={addRow}
          className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-semibold">
          <Plus size={15} /> Add Agenda Item
        </button>
        <div className="flex-1 max-w-lg">
          <input className={`${fi} w-full`} placeholder="General notes / additional context (optional)"
            value={form.notes} onChange={e => sf({ notes: e.target.value })} />
        </div>
      </div>

      {/* Staged files list */}
      {stagedFiles.length > 0 && (
        <div className="px-6 py-3 border-t border-slate-100 space-y-1.5">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">
            Files to attach ({stagedFiles.length})
          </p>
          {stagedFiles.map((f, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-slate-200 text-xs">
              <FileText size={12} className="text-primary-500 flex-shrink-0" />
              <span className="flex-1 font-medium text-slate-700 truncate">{f.name}</span>
              <span className="text-slate-400 flex-shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
              <button onClick={() => removeStagedFile(i)}
                className="p-0.5 hover:bg-red-50 text-red-400 rounded transition-colors flex-shrink-0">
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

// ─── Report View Modal ────────────────────────────────────────────────────────
function ReportViewModal({ report, onEdit, onClose, onTogglePublish, onFollowUpChange }) {
  const { user } = useAuth();
  const totalItems   = (report.items || []).length;
  const pendingCount = (report.items || []).filter(i => i.follow_up_status === 'pending').length;
  const doneCount    = (report.items || []).filter(i => i.follow_up_status === 'completed').length;

  const [attachments, setAttachments] = useState([]);
  const [uploading,   setUploading]   = useState(false);
  const fileRef = React.useRef();

  // New remarks input per item — always starts empty (existing remarks are read-only)
  const [newRemarks,  setNewRemarks]  = useState({});
  const [savingItem, setSavingItem] = useState(null);

  const handleSaveRemarks = async (item) => {
    const added = (newRemarks[item.id] || '').trim();
    if (!added) return;
    setSavingItem(item.id);
    try {
      await onFollowUpChange(report.id, item.id, {
        followUpStatus: item.follow_up_status,
        newRemark:   added,
        remarkedBy:  user?.name || '',
      });
      onClose();
    } catch { /* toast handled by parent */ }
    finally { setSavingItem(null); }
  };

  useEffect(() => {
    dailyReportsAPI.getAttachments(report.id)
      .then(r => setAttachments(r.data))
      .catch(() => {});
  }, [report.id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await dailyReportsAPI.uploadAttachment(report.id, fd);
      const r = await dailyReportsAPI.getAttachments(report.id);
      setAttachments(r.data);
    } catch { /* silent */ }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteAtt = async (attId) => {
    await dailyReportsAPI.deleteAttachment(report.id, attId);
    setAttachments(a => a.filter(x => x.id !== attId));
  };

  const backendBase = window.location.hostname === 'localhost'
    ? 'http://localhost:5000'
    : `${window.location.protocol}//${window.location.hostname}:5000`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-6 px-4"
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl">

        {/* ── Sticky modal header ── */}
        <div className="sticky top-0 z-10 bg-white rounded-t-2xl flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-800">Meeting Report</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {report.report_type} &nbsp;·&nbsp; {fmtDate(report.report_date)}
              {report.created_by_name && <> &nbsp;·&nbsp; {report.created_by_name}</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Report body ── */}
        <div className="p-6 space-y-6">

          {/* Document header band */}
          <div className="rounded-xl overflow-hidden border border-slate-200">
            <div className="bg-gradient-to-r from-primary-700 to-primary-500 px-5 py-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-semibold text-primary-200 uppercase tracking-widest">The Kulish School</p>
                <h3 className="text-base font-bold text-white leading-tight">{report.report_type}
                  {report.title && <span className="ml-2 text-primary-200 font-normal text-sm">· {report.title}</span>}
                </h3>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full font-semibold border capitalize flex-shrink-0 ${statusPill(report.status)}`}>
                {report.status}
              </span>
            </div>
            <div className="bg-slate-50 px-6 py-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm border-t border-slate-200">
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Date</p>
                <p className="font-medium text-slate-700">{fmtDate(report.report_date)}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Created By</p>
                <p className="font-medium text-slate-700">{report.created_by_name || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Agenda Items</p>
                <p className="font-medium text-slate-700">{totalItems}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Follow-ups</p>
                <p className="font-medium text-slate-700">
                  <span className="text-amber-600">{pendingCount} pending</span>
                  <span className="text-slate-300 mx-1">·</span>
                  <span className="text-emerald-600">{doneCount} done</span>
                </p>
              </div>
            </div>
          </div>

          {/* single hidden file input for report-level attachments */}
          <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} />

          {/* Agenda items */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Agenda Items &amp; Action Points</p>
            {(report.items || []).length === 0 ? (
              <div className="text-center py-10 text-slate-400 border border-dashed border-slate-200 rounded-xl">
                <FileText size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No agenda items recorded.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(report.items || []).map((item, idx) => (
                  <div key={item.id || idx}
                    className="border border-slate-200 rounded-xl overflow-hidden">
                    {/* Item header */}
                    {/* Item header */}
                    <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span className="font-semibold text-slate-800 text-sm flex-1">{item.topic || '(No topic)'}</span>
                      {/* Completed = locked pill; otherwise selectable dropdown */}
                      {item.follow_up_status === 'completed' ? (
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${followUpColor('completed')}`}>
                          Completed
                        </span>
                      ) : (
                        <select
                          className={`text-xs font-medium px-2.5 py-1 rounded-full border cursor-pointer outline-none ${followUpColor(item.follow_up_status)}`}
                          value={item.follow_up_status}
                          onChange={e => onFollowUpChange(report.id, item.id, e.target.value)}>
                          {FOLLOW_UP_OPTIONS.filter(o => o.value === 'in_progress' || o.value === 'completed')
                            .map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      )}
                    </div>

                    {/* Item body */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                      <div className="px-4 py-3">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Discussion Summary</p>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                          {item.discussion_summary || <span className="text-slate-300">—</span>}
                        </p>
                      </div>
                      <div className="px-4 py-3">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Action Points</p>
                        <ActionText text={item.action_points} />
                      </div>
                    </div>

                    {/* Remarks section */}
                    <div className="px-4 py-3 border-t border-slate-100 bg-amber-50/30">
                      <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1.5">Remarks</p>

                      {/* Existing remarks — each entry in its own timestamped card */}
                      {parseRemarks(item.remarks).map((r, ri) => (
                        <div key={ri} className="mb-1.5 bg-white border border-slate-200 rounded-lg px-3 py-2">
                          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{r.text}</p>
                          <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1.5">
                            {r.by && <><span className="font-medium text-slate-500">{r.by}</span><span>·</span></>}
                            {r.at ? fmtDateTime(r.at) : <span className="italic">No timestamp</span>}
                          </p>
                        </div>
                      ))}

                      {/* Add new remarks — when In Progress or Completed */}
                      {(item.follow_up_status === 'in_progress' || item.follow_up_status === 'completed') && (
                        <div className="flex gap-2 items-start">
                          <textarea
                            rows={4}
                            placeholder="Add new remarks…"
                            className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white resize-y min-h-[80px]"
                            value={newRemarks[item.id] ?? ''}
                            onChange={e => setNewRemarks(m => ({ ...m, [item.id]: e.target.value }))}
                          />
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleSaveRemarks(item)}
                              disabled={savingItem === item.id || !(newRemarks[item.id] || '').trim()}
                              className="px-3 py-2 text-xs rounded-lg flex items-center gap-1.5 font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 transition-colors whitespace-nowrap">
                              {savingItem === item.id ? <><Clock size={12} /> Saving…</> : <><Save size={12} /> Save</>}
                            </button>
                            <button onClick={() => fileRef.current?.click()} disabled={uploading}
                              className="px-3 py-2 text-xs rounded-lg flex items-center gap-1.5 font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 transition-colors whitespace-nowrap">
                              <Plus size={12} /> {uploading ? 'Uploading…' : 'Upload File'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Pending / Overdue — prompt to set In Progress first */}
                      {(item.follow_up_status === 'pending' || item.follow_up_status === 'overdue') && (
                        <p className="text-xs text-slate-400 italic">
                          Set status to <span className="font-medium text-blue-600">In Progress</span> to add remarks.
                        </p>
                      )}

                      {/* Inline attachment list */}
                      {attachments.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {attachments.map(att => (
                            <div key={att.id} className="flex items-start gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 text-xs">
                              <FileText size={12} className="text-primary-500 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-700 truncate">{att.original_name}</p>
                                <p className="text-slate-400 mt-0.5">
                                  {att.file_size && <>{(att.file_size / 1024).toFixed(1)} KB · </>}
                                  {att.uploaded_by_name && <>{att.uploaded_by_name} · </>}
                                  {fmtDateTime(att.created_at)}
                                </p>
                              </div>
                              <a href={`${backendBase}/uploads/reports/${att.file_name}`}
                                target="_blank" rel="noreferrer"
                                className="text-primary-600 hover:underline font-medium flex-shrink-0">Download</a>
                              <button onClick={() => handleDeleteAtt(att.id)}
                                className="p-0.5 hover:bg-red-50 text-red-400 rounded transition-colors flex-shrink-0">
                                <X size={11} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Meta row */}
                    {(item.due_date || item.assigned_to) && (
                      <div className="flex items-center gap-4 px-4 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
                        {item.due_date && (
                          <span className="flex items-center gap-1.5">
                            <Calendar size={11} className="text-slate-400" />
                            Due: <span className="font-medium text-slate-700">{fmtDate(item.due_date)}</span>
                          </span>
                        )}
                        {item.assigned_to && (
                          <span className="flex items-center gap-1.5">
                            <User size={11} className="text-slate-400" />
                            Assigned to: <span className="font-medium text-slate-700">{item.assigned_to}</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          {report.notes && (
            <div className="border-l-4 border-amber-300 bg-amber-50 px-4 py-3 rounded-r-xl">
              <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-1">Additional Notes</p>
              <p className="text-sm text-slate-700">{report.notes}</p>
            </div>
          )}


        </div>
      </div>
    </div>
  );
}

// ─── Setup Tab ────────────────────────────────────────────────────────────────
function SetupTab({ toast }) {
  const [settings, setSettings] = useState({
    followup_reminders_enabled: 'false',
    followup_reminder_days:     '3',
    followup_notify_method:     'telegram',
    notify_on_publish:          'true',
    telegram_chat_id:           '',
    email_recipients:           '',
  });
  const [followUps, setFollowUps] = useState([]);
  const [fuLoading, setFuLoading] = useState(true);
  const [fuFilter, setFuFilter] = useState('pending');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dailyReportsAPI.getSettings().then(r => {
      if (r.data && Object.keys(r.data).length) setSettings(s => ({ ...s, ...r.data }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setFuLoading(true);
    dailyReportsAPI.getFollowUps({ yearId: 3, status: fuFilter || undefined })
      .then(r => setFollowUps(r.data))
      .catch(() => {})
      .finally(() => setFuLoading(false));
  }, [fuFilter]);

  const save = async () => {
    setSaving(true);
    try { await dailyReportsAPI.saveSettings(settings); toast('Settings saved'); }
    catch (e) { toast(e.response?.data?.error || e.message, 'error'); }
    finally { setSaving(false); }
  };

  const ss = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const Toggle = ({ field, label }) => (
    <label className="flex items-center gap-3 cursor-pointer">
      <div onClick={() => ss(field, settings[field] === 'true' ? 'false' : 'true')}
        className={`relative w-10 h-5 rounded-full transition-colors ${settings[field] === 'true' ? 'bg-primary-500' : 'bg-slate-300'}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${settings[field] === 'true' ? 'translate-x-5' : ''}`} />
      </div>
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );

  return (
    <div className="space-y-5">
      {/* Follow-up Reminders */}
      <div className="glass-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={16} className="text-primary-600" />
          <h3 className="font-semibold text-slate-700">Follow-up Reminders</h3>
        </div>
        <Toggle field="followup_reminders_enabled" label="Enable automatic follow-up reminders" />
        <div className="grid grid-cols-2 gap-4 pl-1">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 uppercase tracking-wide">Remind days before due date</label>
            <select className={`${fi}`} value={settings.followup_reminder_days} onChange={e => ss('followup_reminder_days', e.target.value)}>
              {[1,2,3,5,7,14].map(d => <option key={d} value={d}>{d} day{d>1?'s':''}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 uppercase tracking-wide">Notification method</label>
            <select className={`${fi}`} value={settings.followup_notify_method} onChange={e => ss('followup_notify_method', e.target.value)}>
              <option value="telegram">Telegram</option>
              <option value="email">Email</option>
              <option value="both">Both</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="glass-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-primary-600" />
          <h3 className="font-semibold text-slate-700">Notification Settings</h3>
        </div>
        <Toggle field="notify_on_publish" label="Send notification when a report is published" />
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 uppercase tracking-wide">Telegram Chat ID / Group</label>
            <input className={`${fi}`} placeholder="-100xxxxxxxxxx"
              value={settings.telegram_chat_id} onChange={e => ss('telegram_chat_id', e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 uppercase tracking-wide">Email Recipients (comma-separated)</label>
            <input className={`${fi}`} placeholder="principal@school.com, hod@school.com"
              value={settings.email_recipients} onChange={e => ss('email_recipients', e.target.value)} />
          </div>
        </div>
        <div className="flex">
          <button onClick={save} disabled={saving}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 flex items-center gap-2">
            <Save size={14} /> {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Follow-up Tracker */}
      <div className="glass-card p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CheckCheck size={16} className="text-primary-600" />
            <h3 className="font-semibold text-slate-700">Follow-up Tracker</h3>
          </div>
          <select className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
            value={fuFilter} onChange={e => setFuFilter(e.target.value)}>
            <option value="">All</option>
            {FOLLOW_UP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {fuLoading ? (
          <p className="text-slate-400 text-sm text-center py-4">Loading…</p>
        ) : followUps.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">No follow-up items found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                <tr>
                  <th className="py-2 text-left pr-4">Report</th>
                  <th className="py-2 text-left pr-4">Topic</th>
                  <th className="py-2 text-left pr-4">Action Points</th>
                  <th className="py-2 text-left pr-4">Assigned To</th>
                  <th className="py-2 text-left pr-4">Due Date</th>
                  <th className="py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {followUps.map(f => (
                  <tr key={f.id} className="hover:bg-slate-50">
                    <td className="py-2.5 pr-4 text-slate-500 text-xs whitespace-nowrap">
                      {f.report_type}<br /><span className="text-slate-400">{fmtDate(f.report_date)}</span>
                    </td>
                    <td className="py-2.5 pr-4 font-medium text-slate-700">{f.topic || '—'}</td>
                    <td className="py-2.5 pr-4 text-slate-500 max-w-xs truncate">{f.action_points || '—'}</td>
                    <td className="py-2.5 pr-4 text-slate-500">{f.assigned_to || '—'}</td>
                    <td className="py-2.5 pr-4 text-slate-500 whitespace-nowrap">{fmtDate(f.due_date)}</td>
                    <td className="py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${followUpColor(f.follow_up_status)}`}>
                        {FOLLOW_UP_OPTIONS.find(o => o.value === f.follow_up_status)?.label || f.follow_up_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Reports() {
  const { user, isAdmin, isSuperAdmin, canDoAction } = useAuth();
  const REPORT_CREATOR_ROLES = ['super_admin', 'admin', 'management', 'principal', 'vice_principal', 'coordinator'];
  const canAdd    = REPORT_CREATOR_ROLES.includes(user?.role) || canDoAction('reports', 'add');
  const canEdit   = canDoAction('reports', 'edit') || isAdmin || isSuperAdmin;
  const canDelete = canDoAction('reports', 'delete') || isAdmin || isSuperAdmin;
  const { toasts, add: toast } = useToast();
  const { yearId } = useContext(YearContext);

  const [activeTab, setActiveTab]   = useState('dashboard');
  const [stats,     setStats]       = useState(null);
  const [reports, setReports]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [viewReport, setViewReport] = useState(null);   // full report object (with items)
  const [editMode, setEditMode]     = useState(false);  // editing viewReport
  const [creating, setCreating]     = useState(false);
  const [confirmId, setConfirmId]   = useState(null);

  // filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterFrom,   setFilterFrom]   = useState('');
  const [filterTo,     setFilterTo]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await dailyReportsAPI.getReports({
        yearId: 3,
        status: filterStatus || undefined,
        type:   filterType   || undefined,
        from:   filterFrom   || undefined,
        to:     filterTo     || undefined,
        limit: 50,
      });
      setReports(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast(e.response?.data?.error || e.message, 'error'); }
    finally { setLoading(false); }
  }, [filterStatus, filterType, filterFrom, filterTo]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    dailyReportsAPI.getStats({ yearId: yearId || 3 })
      .then(r => setStats(r.data))
      .catch(() => {});
  }, [yearId]);

  const openReport = async (id) => {
    try {
      const r = await dailyReportsAPI.getReport(id);
      setViewReport(r.data);
      setEditMode(false);
    } catch (e) { toast(e.response?.data?.error || e.message, 'error'); }
  };

  const handleSave = async (formData) => {
    const { stagedFiles = [], ...reportData } = formData;
    try {
      let reportId;
      if (editMode && viewReport) {
        await dailyReportsAPI.updateReport(viewReport.id, reportData);
        reportId = viewReport.id;
        toast('Report updated');
        openReport(viewReport.id);
      } else {
        const r = await dailyReportsAPI.createReport(reportData);
        reportId = r.data.id;
        toast('Report created');
        setCreating(false);
        openReport(reportId);
      }
      // Upload any staged files
      for (const sf of stagedFiles) {
        const fd = new FormData();
        fd.append('file', sf.file);
        await dailyReportsAPI.uploadAttachment(reportId, fd);
      }
      load();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.message || e.message || 'Failed to save report';
      console.error('Save report error:', e.response?.status, msg);
      toast(msg, 'error');
    }
  };

  const handleDelete = async (id) => {
    try { await dailyReportsAPI.deleteReport(id); toast('Deleted'); setConfirmId(null); load(); if (viewReport?.id === id) setViewReport(null); }
    catch (e) { toast(e.response?.data?.error || e.message, 'error'); }
  };

  const handleTogglePublish = async () => {
    const newStatus = viewReport.status === 'published' ? 'draft' : 'published';
    try {
      await dailyReportsAPI.publishReport(viewReport.id, newStatus);
      toast(newStatus === 'published' ? 'Report published' : 'Reverted to draft');
      openReport(viewReport.id);
      load();
    } catch (e) { toast(e.response?.data?.error || e.message, 'error'); }
  };

  const handleFollowUpChange = async (reportId, itemId, payload) => {
    try {
      await dailyReportsAPI.updateFollowUp(reportId, itemId, payload);
      const patch = typeof payload === 'string'
        ? { follow_up_status: payload }
        : { follow_up_status: payload.followUpStatus ?? undefined, remarks: payload.remarks ?? undefined };
      setViewReport(r => r ? { ...r, items: r.items.map(i => i.id === itemId ? { ...i, ...patch } : i) } : r);
      if (typeof payload === 'string') toast('Status updated');
    } catch (e) { toast(e.response?.data?.error || e.message, 'error'); }
  };

  const o  = stats?.overall  || {};
  const it = stats?.items    || {};
  const byTypeData    = (stats?.byType    || []).map(r => ({ name: r.type.replace(' Report','').replace(' Meeting',''), count: r.count }));
  const monthlyData   = (stats?.monthly   || []).map(r => ({ month: `M${r.month}`, total: r.total }));
  const byAssigneeData= (stats?.byAssignee|| []).map(r => ({ name: r.name?.split(' ')[0], items: r.items }));
  const followUpPie   = [
    { name: 'Pending',     value: Number(it.pending     || 0) },
    { name: 'In Progress', value: Number(it.in_progress || 0) },
    { name: 'Completed',   value: Number(it.completed   || 0) },
    { name: 'Overdue',     value: Number(it.overdue     || 0) },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-5 animate-fade-in">
      <ToastList toasts={toasts} />

      {/* ── Page header with tab navigation ── */}
      <div className="space-y-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-display font-bold text-slate-800">Reports</h2>
            <p className="text-slate-500 text-sm mt-0.5">Principal's reports, meeting minutes &amp; follow-up tracker</p>
          </div>
          {/* Tab switcher */}
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm shrink-0">
            {TABS.filter(t => t.id !== 'setup' || isSuperAdmin).map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                  activeTab === t.id ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Dashboard Tab ── */}
      {activeTab === 'dashboard' && (
        <div className="space-y-5">
          {/* Report stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard title="Total Reports" value={Number(o.total_reports || 0)} icon={FileText}    color="blue"   />
            <StatCard title="Published"      value={Number(o.published    || 0)} icon={Send}        color="green"  />
            <StatCard title="Drafts"         value={Number(o.drafts       || 0)} icon={Save}        color="yellow" />
            <StatCard title="Action Items"   value={Number(it.total_items || 0)} icon={Target}      color="indigo" />
          </div>

          {/* Follow-up status cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard title="Pending"     value={Number(it.pending     || 0)} icon={Clock}        color="orange" />
            <StatCard title="In Progress" value={Number(it.in_progress || 0)} icon={TrendingUp}   color="blue"   />
            <StatCard title="Completed"   value={Number(it.completed   || 0)} icon={CheckCircle2} color="green"  />
            <StatCard title="Overdue"     value={Number(it.overdue     || 0)} icon={AlertCircle}  color="red"    />
          </div>

          {/* Charts row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BarChartComponent
              title="Reports by Type"
              data={byTypeData} xKey="name"
              bars={[{ key: 'count', label: 'Reports', color: '#0ea5e9' }]}
            />
            <PieChartComponent
              title="Follow-up Status Breakdown"
              data={followUpPie} donut
            />
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LineChartComponent
              title="Monthly Reports Trend"
              data={monthlyData} xKey="month"
              lines={[{ key: 'total', label: 'Reports', color: '#06b6d4' }]}
            />
            <BarChartComponent
              title="Action Items by Assignee"
              data={byAssigneeData} xKey="name"
              bars={[{ key: 'items', label: 'Items', color: '#84cc16' }]}
            />
          </div>
        </div>
      )}

      {/* ── Daily Reports Tab ── */}
      {activeTab === 'daily_reports' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex flex-wrap gap-2">
              <select className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-600"
                value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                {REPORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-600"
                value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <input type="date" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-600"
                value={filterFrom} onChange={e => setFilterFrom(e.target.value)} title="From date" />
              <input type="date" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-600"
                value={filterTo} onChange={e => setFilterTo(e.target.value)} title="To date" />
            </div>
            {canAdd && !creating && !editMode && (
              <button onClick={() => { setCreating(true); setViewReport(null); }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                <Plus size={14} /> New Report
              </button>
            )}
          </div>

          {/* Create form */}
          {creating && (
            <ReportForm
              onSave={handleSave}
              onCancel={() => setCreating(false)}
            />
          )}

          {/* Edit form */}
          {editMode && viewReport && (
            <ReportForm
              initial={{
                reportType: viewReport.report_type,
                reportDate: (viewReport.report_date || '').split('T')[0],
                title:      viewReport.title || '',
                notes:      viewReport.notes || '',
                status:     viewReport.status,
                items:      viewReport.items?.length
                              ? viewReport.items.map(it => ({ ...it, due_date: toDateStr(it.due_date) }))
                              : [emptyItem()],
              }}
              onSave={handleSave}
              onCancel={() => setEditMode(false)}
            />
          )}


          {/* Report list */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <p className="text-sm text-slate-500">{total} report{total !== 1 ? 's' : ''}</p>
            </div>
            {loading ? (
              <p className="text-center py-10 text-slate-400">Loading…</p>
            ) : reports.length === 0 ? (
              <div className="text-center py-12 space-y-2">
                <FileText size={32} className="mx-auto text-slate-300" />
                <p className="text-slate-400">No reports yet. Click <strong>+ New Report</strong> to create one.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="px-5 py-3 text-left">Date</th>
                    <th className="px-5 py-3 text-left">Report Type</th>
                    <th className="px-5 py-3 text-left">Title</th>
                    <th className="px-5 py-3 text-left">Items</th>
                    <th className="px-5 py-3 text-left">Task Status</th>
                    <th className="px-5 py-3 text-left">Created By</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {reports.map(r => (
                    <tr key={r.id}
                      className={`hover:bg-slate-50 cursor-pointer ${viewReport?.id === r.id ? 'bg-primary-50/30' : ''}`}
                      onClick={() => openReport(r.id)}>
                      <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{fmtDate(r.report_date)}</td>
                      <td className="px-5 py-3 font-medium text-slate-700">{r.report_type}</td>
                      <td className="px-5 py-3 text-slate-500 max-w-xs truncate">{r.title || '—'}</td>
                      <td className="px-5 py-3 text-center">
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{r.item_count}</span>
                      </td>
                      <td className="px-5 py-3">
                        {r.task_status
                          ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${followUpColor(r.task_status)}`}>
                              {FOLLOW_UP_OPTIONS.find(o => o.value === r.task_status)?.label || r.task_status}
                            </span>
                          : <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize border ${statusPill(r.status)}`}>{r.status}</span>
                        }
                      </td>
                      <td className="px-5 py-3 text-slate-500">{r.created_by_name || '—'}</td>
                      <td className="px-5 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openReport(r.id)}
                            className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"><Eye size={14} /></button>
                          {canDelete && (
                            <button onClick={() => setConfirmId(r.id)}
                              className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Setup Tab ── */}
      {activeTab === 'setup' && isSuperAdmin && <SetupTab toast={toast} />}

      {/* ── Report View Modal ── */}
      {viewReport && !editMode && (
        <ReportViewModal
          report={viewReport}
          onEdit={() => setEditMode(true)}
          onClose={() => setViewReport(null)}
          onTogglePublish={handleTogglePublish}
          onFollowUpChange={handleFollowUpChange}
        />
      )}

      {/* Delete confirm */}
      {confirmId && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-slate-200 w-80 space-y-4">
            <p className="font-semibold text-slate-800">Delete this report?</p>
            <p className="text-sm text-slate-500">All agenda items and action points in this report will be permanently deleted.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmId(null)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDelete(confirmId)} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
